import axios from 'axios';
import { authStorage } from './storage';

// Auto-detect API URL based on environment
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

console.log('[API] Base URL:', API_BASE_URL);

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Token interceptor
api.interceptors.request.use(
  (config) => {
    const token = authStorage.getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      console.log('[API] 401 Unauthorized - clearing auth');
      await authStorage.clearAuth();
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  signup: (email, password, name) =>
    api.post('/auth/signup', { email, password, name }),
  
  login: async (email, password) => {
    console.log('[API] Attempting login for:', email);
    
    // Clear any old auth first
    await authStorage.clearAuth();
    
    // OAuth2 format expects username field
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        timeout: 30000,
      });
      
      console.log('[API] Login response status:', response.status);
      console.log('[API] Has access_token:', !!response.data?.access_token);
      
      if (response.data?.access_token) {
        await authStorage.saveToken(response.data.access_token);
        console.log('[API] Token saved');
      } else {
        throw new Error('No access token in response');
      }
      
      return response;
    } catch (error) {
      console.error('[API] Login error:', error.message);
      throw error;
    }
  },
  
  getCurrentUser: async () => {
    const token = authStorage.getToken();
    console.log('[API] Getting current user, hasToken:', !!token);
    
    if (!token) {
      throw new Error('No token available');
    }
    
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        timeout: 30000,
      });
      
      console.log('[API] getCurrentUser response:', response.status);
      
      if (response.data) {
        await authStorage.saveUser(response.data);
      }
      
      return response;
    } catch (error) {
      console.error('[API] getCurrentUser error:', error.message);
      throw error;
    }
  },
  
  logout: async () => {
    await authStorage.clearAuth();
    return Promise.resolve();
  },
};

// Personal Finance API
export const personalAPI = {
  addIncome: (data) => api.post('/personal/income', data),
  listIncome: (limit = 10) => api.get(`/personal/income?limit=${limit}`),
  deleteIncome: (id) => api.delete(`/personal/income/${id}`),
  addExpense: (data) => api.post('/personal/expenses', data),
  listExpenses: (limit = 10, category = null) => {
    const url = category 
      ? `/personal/expenses?limit=${limit}&category=${category}`
      : `/personal/expenses?limit=${limit}`;
    return api.get(url);
  },
  deleteExpense: (id) => api.delete(`/personal/expenses/${id}`),
  getSummary: (month = null) => {
    const url = month ? `/personal/summary?month=${month}` : '/personal/summary';
    return api.get(url);
  },
};

// Household API
export const householdAPI = {
  create: (name) => api.post('/household', { name }),
  join: (inviteCode) => api.post('/household/join', { invite_code: inviteCode }),
  list: () => api.get('/household'),
  listDeleted: () => api.get('/household/deleted/list'),
  delete: (householdId, permanent = false) => 
    api.delete(`/household/${householdId}?permanent=${permanent}`),
  restore: (householdId) => api.post(`/household/${householdId}/restore`),
  getExpenseCount: async (householdId) => {
    try {
      const response = await api.get(`/household/${householdId}/expenses?limit=10000`);
      return response.data.length;
    } catch (error) {
      console.error('Error counting expenses:', error);
      return 0;
    }
  },
  getMembers: (householdId) => api.get(`/household/${householdId}/members`),
  addExpense: (householdId, data) => api.post(`/household/${householdId}/expenses`, data),
  listExpenses: (householdId, limit = 10) => 
    api.get(`/household/${householdId}/expenses?limit=${limit}`),
  addContribution: (householdId, data) => 
    api.post(`/household/${householdId}/contributions`, data),
  getSummary: (householdId, month = null) => {
    const url = month 
      ? `/household/${householdId}/summary?month=${month}`
      : `/household/${householdId}/summary`;
    return api.get(url);
  },
};

// Wallet API
export const walletAPI = {
  create: (data) => api.post('/wallets', data),
  list: () => api.get('/wallets'),
  get: (walletId) => api.get(`/wallets/${walletId}`),
  delete: (walletId, permanent = false) => 
    api.delete(`/wallets/${walletId}?permanent=${permanent}`),
  listDeleted: () => api.get('/wallets/deleted/list'),
  restore: (walletId) => api.post(`/wallets/${walletId}/restore`),
  getTransactionCount: async (walletId) => {
    try {
      const [expenses, income] = await Promise.all([
        api.get(`/personal/expenses?limit=10000`),
        api.get(`/personal/income?limit=10000`)
      ]);
      const expenseCount = expenses.data.filter(e => e.wallet_id === walletId).length;
      const incomeCount = income.data.filter(i => i.wallet_id === walletId).length;
      return expenseCount + incomeCount;
    } catch (error) {
      console.error('Error counting transactions:', error);
      return 0;
    }
  },
  exchange: (data) => api.post('/wallets/exchange', data),
  listExchanges: (limit = 20) => api.get(`/wallets/exchange/list?limit=${limit}`),
};

export default api;
